<template>
  <div class="w-full h-full p-2 bg-white">
    <div class="flex flex-col w-full h-full px-4">
      <div>
        <div class="font-bold">{{ $t('pages.roles') }}</div>
        <div class="my-2 font-bold">
          <button
            class="p-2 rounded-lg text-theme-600 hover:bg-theme-200"
            @click="isModified ? (modalCloseWarningActive = true) : onBack()">
            <IconArrowLeft />
          </button>
          {{ $t('pages.role-details') }}
        </div>
        <div class="text-sm">{{ $t('settings.roleDetailInfo') }}</div>
      </div>
      <div class="flex-1 mt-4" v-if="roleDetails">
        <div class="flex w-full gap-4">
          <div class="w-full">
            <label for="name" class="text-sm font-medium">{{ $t('baseObject.code') }}</label>
            <BaseInput
              v-model="roleDetails.code"
              color="base"
              class="rounded-md text-theme-700"
              :disabled="!isAdminUser"
              show-error-as-tooltip
              :error="$v.code.required.$invalid ? $v.code.required.$message : ''">
            </BaseInput>
          </div>
          <div class="w-full">
            <label for="name" class="text-sm font-medium">{{ $t('baseObject.name') }}</label>
            <BaseInput
              color="base"
              class="rounded-md text-theme-700"
              v-model="roleDetails.name"
              :disabled="!isAdminUser"
              show-error-as-tooltip
              :error="$v.name.required.$invalid ? $v.name.required.$message : ''">
            </BaseInput>
          </div>
          <div class="w-full">
            <div class="flex flex-col h-full">
              <label for="name" class="text-sm font-medium">{{ $t('baseObject.isActive') }}</label>
              <BaseTableCellStatus
                class="flex items-center flex-1"
                v-if="!isAdminUser"
                :item="roleDetails.isActive ? roleDetails.isActive : 0" />
              <BaseToggle
                v-else
                :label="$t('baseObject.isActive')"
                name="isActive"
                class="flex-1"
                v-model="roleDetails.isActive" />
            </div>
          </div>
          <div class="w-full">
            <div class="flex flex-col h-full">
              <label for="name" class="text-sm font-medium">{{ $t('baseObject.deleted') }}</label>
              <BaseTableCellStatus
                class="flex items-center flex-1"
                v-if="!isAdminUser"
                :textTrue="$t('common.actions.yes')"
                :textFalse="$t('common.actions.no')"
                :item="roleDetails.deleted ? roleDetails.deleted : 0" />
              <BaseToggle
                v-else
                :label="$t('baseObject.deleted')"
                name="deleted"
                class="flex-1"
                v-model="roleDetails.deleted" />
            </div>
          </div>
        </div>
        <div class="flex justify-between gap-2 mt-4">
          <BaseTableControls
            :is-saving-layout="false"
            :isSavingLayoutHidden="true"
            :isFilterButtonHidden="true"
            :is-search-hidden="true"
            :is-download-button-hidden="true"
            :is-refresh-button-hidden="true"
            :isAddNewHidden="roleDetails.isFromManager ? false : true"
            :add-button-text="$t('settings.addModule')"
            class="justify-between"
            @add-new="roleDetails.isFromManager ? onAddNew() : ''">
            <template #default>
              <slot name="controls"
                ><button
                  v-if="isAdminUser"
                  @click="onResetAll"
                  class="px-2 py-1 font-normal border border-gray-300 rounded-md text-theme-500">
                  {{ $t('common.table.resetFilters') }}
                </button></slot
              >
            </template>
          </BaseTableControls>
        </div>
        <div>
          <table class="w-full" v-if="transformedModules">
            <thead class="text-xs font-normal text-gray-500 uppercase bg-gray-50">
              <tr class="my-4">
                <th class="w-[25%]" v-for="header in tableHeaders" :key="header.text">
                  {{ header.text }}
                </th>
              </tr>
            </thead>
            <tbody
              v-for="applicationId in Object.keys(transformedModules)"
              :key="applicationId"
              class="text-xs bg-white border divide-y divide-gray-300">
              <tr>
                <td
                  colspan="6"
                  class="py-1.5 text-center border-b-2 bg-theme-100 text-theme-500 border-theme-600">
                  {{ getApplicationName(applicationId) }}
                </td>
              </tr>
              <tr>
                <td></td>
                <td class="pl-[11%]">
                  <div class="flex justify-start gap-2 my-1">
                    <BaseCheckbox
                      :disabled="!isAdminUser"
                      :model-value="
                        getSelectAllValue(transformedModules[applicationId], 'isBinaryAllowed')
                      "
                      @change="
                        handleAllCheck(transformedModules[applicationId], 'isBinaryAllowed', $event)
                      "></BaseCheckbox>
                    {{ $t('settings.all') }}
                  </div>
                </td>
                <td class="bg-theme-100 pl-[11%]">
                  <div class="flex justify-start gap-2 my-1">
                    <BaseCheckbox
                      :disabled="!isAdminUser"
                      :model-value="
                        getSelectAllValue(transformedModules[applicationId], 'isMobileAllowed')
                      "
                      @change="
                        handleAllCheck(transformedModules[applicationId], 'isMobileAllowed', $event)
                      "></BaseCheckbox>
                    {{ $t('settings.all') }}
                  </div>
                </td>
                <td class="pl-[11%]">
                  <div class="flex justify-start gap-2 my-1">
                    <BaseCheckbox
                      :disabled="!isAdminUser"
                      :model-value="
                        getSelectAllValue(transformedModules[applicationId], 'isActive')
                      "
                      @change="
                        handleAllCheck(transformedModules[applicationId], 'isActive', $event)
                      "></BaseCheckbox>
                    {{ $t('settings.all') }}
                  </div>
                </td>
              </tr>

              <tr v-for="module in transformedModules[applicationId]" :key="module.id">
                <td class="pl-2 font-medium text-gray-700">
                  {{ getModuleName(module.mwModuleID) }}
                </td>
                <td class="pl-[11%]">
                  <div class="flex justify-start gap-2 my-1">
                    <BaseCheckbox
                      :disabled="!isAdminUser"
                      v-model="
                        roleDetails.roleMWModules[module.index ? module.index : 0].isBinaryAllowed
                      "></BaseCheckbox>
                  </div>
                </td>
                <td class="bg-theme-100 pl-[11%]">
                  <div class="flex justify-start gap-2 my-1">
                    <BaseCheckbox
                      :disabled="!isAdminUser"
                      v-model="
                        roleDetails.roleMWModules[module.index ? module.index : 0].isMobileAllowed
                      "></BaseCheckbox>
                  </div>
                </td>
                <td class="pl-[11%] pr-4">
                  <div class="flex justify-between gap-2 my-1">
                    <BaseCheckbox
                      :disabled="!isAdminUser"
                      v-model="
                        roleDetails.roleMWModules[module.index ? module.index : 0].isActive
                      "></BaseCheckbox>
                    <button
                      v-if="roleDetails.isFromManager"
                      type="button"
                      @click="
                        modalDeleteRow = true;
                        deleteRowItem = module;
                      "
                      class="rounded-full p-1.5 text-red-400 hover:bg-red-50">
                      <IconTrashOutline class="size-4" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <table class="w-full"></table>
        </div>

        <div class="flex items-center gap-2 mt-4">
          <BaseButton v-if="isAdminUser" size="sm" class="gap-2 capitalize" @click="onSave">
            {{ $t('common.actions.save') }}
          </BaseButton>
          <BaseButton
            size="sm"
            outline
            class="gap-2 capitalize"
            @click="isModified ? (modalCloseWarningActive = true) : onBack()">
            {{ $t('common.actions.cancel') }}
          </BaseButton>
        </div>

        <ModuleCreateEditModal
          v-if="createEditModalActive"
          :title="$t('settings.addModule')"
          :modules="availableModules ? availableModules : []"
          :applications="availableApplications ? availableApplications : []"
          :mode="createEditModalMode"
          @close-modal="createEditModalActive = false"
          @save="onAddModule" />
        <BaseDrawerConfirm
          :is-active="modalCloseWarningActive"
          @confirm="onBack"
          @cancel="modalCloseWarningActive = false" />
        <BaseModalConfirm
          v-if="modalDeleteRow"
          @cancel="modalDeleteRow = false"
          @confirm="onDeleteRow" />
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, onBeforeMount, ref, watch } from 'vue';
import IconArrowLeft from '@/assets/icons/IconArrowLeft.vue';
import BaseToggle from '@/components/common/BaseToggle.vue';
import BaseInput from '@/components/common/BaseInput.vue';
import BaseCheckbox from '@/components/common/BaseCheckbox.vue';
import BaseButton from '@/components/common/BaseButton.vue';
import BaseTableCellStatus from '@/components/common/BaseTableCellStatus.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import { type RoleMWModule } from '@/types/MW';
import type { RoleDetails } from '@/types/RoleDetails';
import BaseTableControls from '@/components/common/BaseTableControls.vue';
import ModuleCreateEditModal from '@/components/settings/ModuleCreateEditModal.vue';
import BaseDrawerConfirm from '@/components/common/BaseDrawerConfirm.vue';
import { useRoute, useRouter } from 'vue-router';
import { addOrEditRoleDetail, getRoleDetailById } from '@/services/api/user';
import { useToastStore } from '@/stores/toast';
import { useI18n } from 'vue-i18n';
import useVuelidate from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import { RecordMode } from '@/types/Base';
import { useLookupsStore } from '@/stores/lookups';
import { storeToRefs } from 'pinia';
import { deepCopy } from '@/utils/data';

const roleDetails = ref<RoleDetails>();
const originalData = ref<RoleDetails | null>(null);
const createEditModalActive = ref(false);
const createEditModalMode = ref<'add' | 'edit'>('add');

const route = useRoute();
const router = useRouter();
const modalCloseWarningActive = ref(false);
const toastStore = useToastStore();
const { t: $t } = useI18n({ useScope: 'global' });

const isAdminUser = ref(false);
const isModified = computed(() => {
  return JSON.stringify(roleDetails.value) != JSON.stringify(originalData.value);
});

const tableHeaders = [
  { text: $t('settings.modules') },
  { text: $t('settings.desktopAccess') },
  { text: $t('settings.mobileAccess') },
  { text: $t('baseObject.actions') },
];

const { getMwApplicationLookup, getMwModulesLookup, getModuleInstalationsLookup } =
  useLookupsStore();
const { mwModules, mwApplications, moduleInstallations } = storeToRefs(useLookupsStore());

onBeforeMount(async () => {
  if (route.params.id) {
    const res = await getRoleDetailById(route.params.id as string);
    if (res) {
      roleDetails.value = res;
    } else {
      roleDetails.value = {
        id: route.params.id as string,
        name: '',
        code: '',
        isActive: true,
        isFromManager: true,
        deleted: false,
        roleMWModules: [],
      };
    }
  }

  moduleInstallations.value = [];
  await getModuleInstalationsLookup();
  await getMwApplicationLookup();
  await getMwModulesLookup();
  if (roleDetails.value) {
    originalData.value = deepCopy<RoleDetails>(roleDetails.value);
  }
  isAdminUser.value = JSON.parse(localStorage.getItem('sessionDetails')!).isAdmin;
});

interface transformedModule {
  [index: string]: RoleMWModule[];
}

const availableModules = computed(() => {
  const enabledModulesIds = moduleInstallations.value
    .filter((mi) => mi.isEnabled)
    .map((mi) => mi.mwModuleId);
  const occupiedModulesIds = roleDetails.value?.roleMWModules.map((rm) => rm.mwModuleID) ?? [];

  return mwModules.value?.filter(
    (m) => enabledModulesIds.includes(m.id) && !occupiedModulesIds.includes(m.id)
  );
});

const availableApplications = computed(() => {
  return mwApplications.value.filter((a) =>
    moduleInstallations.value.find(
      (i) =>
        i.mwApplicationId === a.id &&
        i.isEnabled &&
        !!availableModules.value.find((m) => m.id === i.mwModuleId)
    )
  );
});

const transformedModules = computed(() => {
  return roleDetails.value?.roleMWModules
    .filter((x) =>
      moduleInstallations.value.find((i) => i.isEnabled && i.mwApplicationId === x.mwApplicationID)
    )
    .reduce((acc, current, index) => {
      if (
        current.rowStatus == RecordMode.Deleted ||
        !moduleInstallations.value.find((i) => i.mwModuleId === current.mwModuleID)?.isEnabled
      )
        return acc;
      if (!acc[current.mwApplicationID]) {
        acc[current.mwApplicationID] = [];
      }
      acc[current.mwApplicationID].push({ ...current, index });
      return acc;
    }, {} as transformedModule);
});

function onAddNew() {
  createEditModalActive.value = true;
  createEditModalMode.value = 'add';
}

function getSelectAllValue(
  modules: RoleMWModule[],
  property: 'isActive' | 'isBinaryAllowed' | 'isMobileAllowed'
) {
  return modules.reduce((acc, cur) => {
    return acc && cur[property];
  }, true);
}
function getApplicationName(id: string) {
  const application = mwApplications.value?.find((e) => e.id == id);
  return application?.name;
}

function getModuleName(id: string) {
  const module = mwModules.value?.find((e) => e.id == id);
  return module?.displayName;
}

function handleAllCheck(
  modules: RoleMWModule[],
  property: 'isActive' | 'isBinaryAllowed' | 'isMobileAllowed',
  event: any
) {
  if (!event || !roleDetails.value) return;
  modules.forEach((element) => {
    if (!roleDetails.value) return;
    roleDetails.value.roleMWModules[element.index ? element.index : 0][property] =
      event.target._modelValue;
  });
}

function onResetAll() {
  if (roleDetails.value && roleDetails.value.roleMWModules) {
    roleDetails.value.roleMWModules = roleDetails.value?.roleMWModules.map((item) => ({
      ...item,
      isActive: false,
      isMobileAllowed: false,
      isBinaryAllowed: false,
    }));
  }
}

function onAddModule(item: RoleMWModule) {
  if (!roleDetails.value) return;
  item.roleID = roleDetails.value.id;
  item.rowStatus = RecordMode.Added;
  roleDetails.value.roleMWModules.push(item);
  createEditModalActive.value = false;
}

async function onSave() {
  $v.value.$touch();
  if ($v.value.$invalid) return;
  if (!roleDetails.value) return;
  const res = await addOrEditRoleDetail(roleDetails.value);
  if (res) {
    toastStore.error({ text: res ? res : $t('common.toast.error') });
  } else {
    toastStore.success({ text: $t('common.toast.success') });
    onBack();
  }
}
function onBack() {
  router.push({ name: 'roles-management' });
}

const modalDeleteRow = ref(false);
const deleteRowItem = ref<RoleMWModule>();

function onDeleteRow() {
  if (!deleteRowItem.value || deleteRowItem.value.index == undefined || !roleDetails.value) return;
  if (deleteRowItem.value?.rowStatus == RecordMode.Added) {
    roleDetails.value?.roleMWModules.splice(deleteRowItem.value.index, 1);
  } else {
    roleDetails.value.roleMWModules[deleteRowItem.value.index].rowStatus = RecordMode.Deleted;
  }

  modalDeleteRow.value = false;
}

const validationModel = ref({
  name: roleDetails.value?.name || '',
  code: roleDetails.value?.code || '',
});

const rules = computed(() => ({
  name: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
  code: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true });

watch(
  () => roleDetails.value,
  (newData: any) => {
    validationModel.value.name = newData.name || '';
    validationModel.value.code = newData.code || '';
  },
  { deep: true }
);
</script>
